import { useTranslation } from "react-i18next";
import { MapPinOff } from "lucide-react";
import type { LapAlignment } from "@/types";

interface AlignmentUnavailableProps {
  alignment: Extract<LapAlignment, { status: "unavailable" }>;
}

/**
 * Explains why the two laps could not be aligned. There is deliberately no
 * fallback: the page says what failed and what was measured.
 */
export default function AlignmentUnavailable({ alignment }: AlignmentUnavailableProps) {
  const { t } = useTranslation("pages");
  const details = Object.entries(alignment.details);

  return (
    <div
      className="flex h-full flex-col items-center justify-center rounded-xl border border-[#1a1a2e] bg-[#0d0d16] p-8 text-center"
      role="status"
      data-testid="alignment-unavailable"
    >
      <MapPinOff className="mb-3 h-10 w-10 text-amber-400" />
      <h2 className="text-base font-semibold text-slate-100">
        {t("lapComparison.unavailable.title")}
      </h2>
      <p className="mt-2 max-w-md text-sm text-slate-300">
        {t(`lapComparison.unavailable.${alignment.reason}`)}
      </p>
      <p className="mt-1 max-w-md text-xs text-slate-500">
        {t("lapComparison.unavailable.methodNote")}
      </p>
      {details.length > 0 && (
        <dl className="mt-4 grid grid-cols-2 gap-x-6 gap-y-1 font-mono text-xs text-slate-500">
          {details.map(([key, value]) => (
            <div key={key} className="contents">
              <dt className="text-left">{key}</dt>
              <dd className="text-right text-slate-300">{String(value)}</dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
}
